import shortid from 'shortid'
import { groupBy, reduce } from 'lodash'

import Node from './node/classe'
import Edge from './edge/classe'

function joinerFactory () {
  let selection = {}

  return (items) => {
    const itemDict = items.reduce((p, c) => {
      p[c.id] = c
      return p
    }, {})

    const enters = items.filter((d) => {
      return !selection[d.id]
    })
    const updates = items.filter((d) => {
      return selection[d.id]
    })
    const exits = reduce(selection, (p, value, c) => {
      if (!itemDict[c]) p.push(value)
      return p
    }, [])

    selection = itemDict

    return {
      enters, updates, exits
    }
  }
}

export default class Manager {
  constructor (nodesLength, edgesLength) {
    this.index = new Float32Array(nodesLength)

    this.nodeIds = {}
    this.indexToNode = {}
    this.nodeIdToIndexDict = {}
    this.nodeIdToNode = {}
    this.selectedNodes = []

    this.edgeIds = {}
    this.indexToEdge = {}
    this.edgeIdToIndexDict = {}
    this.edgeIdToEdge = {}
    this.selectedEdges = []

    this.counts = {}

    this.iterationId = shortid()

    this.nodePositions = new Float32Array(nodesLength * 3)

    const t = new Array(nodesLength).fill().map((_, i) => i)

    this.nodeIndexes = new Float32Array(t)

    this.nodeColors = new Float32Array(nodesLength * 3)
    this.nodeScales = new Float32Array(nodesLength)
    this.nodeOpacities = new Float32Array(nodesLength)

    this.fa2 = {
      distances: new Float32Array(nodesLength * 4),
      masses: new Float32Array(nodesLength),
      convergences: new Float32Array(nodesLength),
      sizes: new Float32Array(nodesLength),
      fixes: new Float32Array(nodesLength),
      edgesIndexes: new Float32Array(edgesLength * 2),
      edgesValues: new Float32Array(edgesLength)
    }

    this.nodes = []
    this.nodesTypes = {}
    this.edges = []

    this.edgeFromPosition = new Float32Array(edgesLength * 3)
    this.edgeToPosition = new Float32Array(edgesLength * 3)
    this.edgeOpacities = new Float32Array(edgesLength)
    this.edgeWidth = new Float32Array(edgesLength)

    this.nodeIdToTempPosition = {}

    this.nodeJoiner = joinerFactory()
    this.edgeJoiner = joinerFactory()
  }

  selectNodes (nodes) {
    const joint = this.nodeJoiner(nodes)
    const selectedNodes = []
    const newPositions = new Float32Array(nodes.length * 3)
    const enterIds = {}

    const setIndex = (node, index) => {
      this.nodeIdToNode[node.data.id] = node

      node.setIndex(index)
    }

    joint.exits.forEach((d) => {
      const node = this.nodeIdToNode[d.id]
      this.nodeIdToTempPosition[d.id] = node.getPosition(this.nodePositions)
    })

    joint.enters.forEach((d) => {
      if (!this.nodeIdToNode[d.id]) {
        const node = new Node(d)
        this.nodeIdToNode[d.id] = node
      }
      enterIds[d.id] = d
    })

    this.selectedNodesId = {}

    const groups = groupBy(nodes, (d) => d.shape)
    const { shapes, flattenNodes } = Object.keys(groups)
      .reduce((p, c) => {
        const group = groups[c]
        const total = groups[c].length

        p.shapes.push({
          type: c,
          bounds: [p.flattenNodes.length, p.flattenNodes.length + total],
          total: total
        })

        p.flattenNodes = p.flattenNodes.concat(group)

        return p
      }, { shapes: [], flattenNodes: [] })

    flattenNodes.forEach((d, index) => {
      const node = this.nodeIdToNode[d.id]
      let oldPosition = enterIds[d.id]
        ? this.nodeIdToTempPosition[d.id]
          ? this.nodeIdToTempPosition[d.id]
          : d.defaultPosition
        : node.getPosition(this.nodePositions)

      if (oldPosition[0] === 0 && oldPosition[1] === 0) {
        oldPosition = d.defaultPosition
      }

      const completeOpts = Object.assign(
        {
          position: [0, 0, 0],
          scale: 1,
          color: [255, 255, 255],
          opacity: 1
        },
        d.style
      )
      completeOpts.fa2 = Object.assign(
        {
          mass: 1,
          convergence: 1,
          size: 1,
          fixed: 0
        },
        d.fa2
      )

      setIndex(node, index)

      node.writePosition(oldPosition, newPositions)
      node.writeColor(completeOpts.color, this.nodeColors)
      node.writeScale(completeOpts.scale, this.nodeScales)
      node.writeOpacity(completeOpts.opacity, this.nodeOpacities)

      node.writeFa2Mass(completeOpts.fa2.mass, this.fa2.masses)
      node.writeFa2Convergence(completeOpts.fa2.convergence, this.fa2.convergences)
      node.writeFa2Size(completeOpts.fa2.size, this.fa2.sizes)
      node.writeFa2Fixed(completeOpts.fa2.fixed, this.fa2.fixes)

      selectedNodes.push(d.id)
      this.selectedNodesId[d.id] = 1
    })

    this.nodePositions.set(newPositions)

    this.counts = {
      ...this.counts,
      nodes: shapes
    }
    this.selectedNodes = selectedNodes
  }

  selectEdges = (edges) => {
    const selectedEdges = []

    edges
      .filter((d) => this.selectedNodesId[d.source] && this.selectedNodesId[d.target])
      .forEach((d, index) => {
        if (!this.edgeIdToEdge[d.id]) {
          this.edgeIdToEdge[d.id] = new Edge(d)
        }

        const edge = this.edgeIdToEdge[d.id]

        const completeOpts = Object.assign(
          {
            opacity: 1,
            width: 1
          },
          d.style
        )

        edge.setIndex(index)

        edge.writeOpacity(completeOpts.opacity, this.edgeOpacities)
        edge.writeWidth(completeOpts.width, this.edgeWidth)

        edge.writeFa2Value(d.fa2.value || 1, this.fa2.edgesValues)
        edge.writeSourceIndex(this.nodeIdToNode[d.source].v1index, this.fa2.edgesIndexes)
        edge.writeTargetIndex(this.nodeIdToNode[d.target].v1index, this.fa2.edgesIndexes)

        selectedEdges.push(d.id)
      })

    this.counts = {
      ...this.counts,
      edges: selectedEdges.length
    }

    this.selectedEdges = selectedEdges
  }

  getNodesById = () => {
    return this.nodeIdToNode
  }

  getSelectNodes = () => {
    return this.selectedNodes.map((d) => this.nodeIdToNode[d])
  }

  getSelectEdges = () => {
    return this.selectedEdges.map((d) => this.edgeIdToEdge[d])
  }

  writePosition = (index, value = [0, 0, 0]) => {
    this.nodePositions[index] = value[0]
    this.nodePositions[index + 1] = value[1]
    this.nodePositions[index + 2] = 0
  }

  writeFa2Position = (index, value = [0, 0, 0]) => {
    this.fa2.positions[index] = value[0]
    this.fa2.positions[index + 1] = value[1]
    this.fa2.positions[index + 2] = 0
  }

  getBuffers = () => {
    return [
      this.nodePositions,
      this.nodeColors,
      this.nodeScales,
      this.nodeOpacities,
      this.nodeIndexes
    ]
  }

  getEdgesBuffers = () => {
    return [
      this.edgesPositions
    ]
  }

  getEdgeBuffers = () => {
    return [
      this.edgeFromPosition,
      this.edgeToPosition,
      this.edgeOpacities,
      this.edgeWidth
    ]
  }

  getFa2Buffers = () => {
    return [
      this.fa2.distances,
      this.fa2.mass,
      this.fa2.convergences,
      this.fa2.sizes,
      this.fa2.fixed
    ]
  }
}
